/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { css, StyleSheet } from 'aphrodite';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AxiosError } from 'axios';
import { DropdownEllipsisBtn } from '@blend/enterprise-components';
import { State } from '../../../../redux';
import { usePermissions } from '../../../../hooks/usePermissions';
import { addApiErrorNotification } from '../../../../actions/pageActions';
import { notaryAPI } from '../../../../api/notary';
import { NotaryAPI } from '../../../../api/notary/types';
import { enterpriseColors } from '../../../../style/colors';
import { SchedulePreferencesCard } from './SchedulePreferencesCard';
import { TabSectionHeader } from './TabSectionHeader';
import { DocsStatusCard } from './DocsStatusCard';
import { NotaryTaskList } from '@components/NotaryTaskList/NotaryTaskList';
import { formatIsoDateInLocalTime } from '@utils/formatting';
import { TabContentTitle } from '@components/TabContentTitle/TabContentTitle';
import { RoomStatus } from '@shared/constants/room';
import { ConfirmationModal } from '@components/common/Modals/ConfirmationModal/ConfirmationModal';

export namespace Overview {
  export interface Props {
    roomId: string;
    onReschedule: () => void;
    notaryId?: string;
    scheduleTime?: string;
    onCancel?: () => void;
  }
}

const styles = StyleSheet.create({
  wrapper: {
    padding: '0 40px',
    position: 'relative',
    width: 600,
  },
  headerWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: 40,
  },
  actionsWrapper: {
    position: 'absolute',
    top: 20,
    right: 10,
  },
});

export const OverviewTab: React.FC<Overview.Props> = ({ roomId, onReschedule, notaryId, scheduleTime, onCancel }) => {
  const dispatch = useDispatch();
  const room = useSelector((reduxState: State) => reduxState.roomDetail.room!);
  const user = useSelector((reduxState: State) => reduxState.user);
  const permissions = usePermissions();
  const [recordings, setRecordings] = useState<NotaryAPI.Recording[]>();
  const [isOpen, setIsOpen] = useState(false);
  const fetchRecordings = async () => {
    try {
      const result = await notaryAPI.rooms.getRecordings(roomId);
      setRecordings(result);
    } catch (error) {
      addApiErrorNotification('Fail to load recording list', error as AxiosError<{ message?: string }>)(dispatch);
    }
  };
  useEffect(() => {
    fetchRecordings();
  }, [roomId]);

  const openCancelModal = () => {
    setIsOpen(true);
  };

  const cancelRoom = async () => {
    try {
      const result = await notaryAPI.rooms.cancelRoom(roomId);
      if (result && result.status === RoomStatus.CANCELED) {
        onCancel && onCancel();
      }
    } catch (err) {
      addApiErrorNotification('Fail to cancel room', err as AxiosError<{ message?: string }>)(dispatch);
    } finally {
      setIsOpen(false);
    }
  };
  const isSuperAdminOrAdmin =
    permissions.includes(NotaryAPI.BSRPermissions.BSRSuperAdmin) ||
    permissions.includes(NotaryAPI.BSRPermissions.BSRAdmin);
  const isCurrentNotary = notaryId === user?.id;

  const formatDuration = (duration?: number): string | undefined => {
    if (typeof duration !== 'number') {
      return;
    }
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return (minutes > 0 ? `${minutes} min ` : '') + `${seconds} sec`;
  };

  const moreActionsButton = room && room.status !== RoomStatus.CANCELED && (isSuperAdminOrAdmin || isCurrentNotary) && (
    <div className={css(styles.actionsWrapper)}>
      <DropdownEllipsisBtn
        actions={[
          {
            label: 'Cancel',
            onClick: openCancelModal,
          },
        ]}
        anchorPosition="bottom-start"
        listBtnSize="lg"
        listMaxHeight={300}
        id="overviewTab-moreActionsButton"
        noBorder
      />
    </div>
  );

  const cancelRoomModal = (
    <ConfirmationModal
      heading={'Are you sure you want to cancel this appointment?'}
      contentLabel={'Cancel Room Modal'}
      isOpen={isOpen}
      confirmButtonText={'Confirm cancellation'}
      handleConfirm={() => {
        cancelRoom();
      }}
      closeButtonText={'Never mind'}
      handleClose={() => {
        setIsOpen(false);
      }}
      buttonColor={enterpriseColors.PRIMARY_500}
    >
      <p>Once cancelled, only the lending institution can resume the loan.</p>
      <p>After this appointment is cancelled, the following actions will become unavailable:</p>
      <ul>
        <li>Confirm an appointment</li>
        <li>Attend an appointment</li>
        <li>Re-assign an appointment</li>
        <li>Change notary group</li>
      </ul>
    </ConfirmationModal>
  );

  return (
    <div data-it={'overviewTab'} className={css(styles.wrapper)}>
      <TabContentTitle>Overview</TabContentTitle>
      {moreActionsButton} {cancelRoomModal}
      <TabSectionHeader>Tasks</TabSectionHeader>
      <NotaryTaskList {...{ onReschedule, notaryId, roomId, scheduleTime }} />
      <TabSectionHeader>Borrower preferences</TabSectionHeader>
      <SchedulePreferencesCard />
      <TabSectionHeader>Document Statuses</TabSectionHeader>
      <DocsStatusCard {...{ roomId }} />
      <TabSectionHeader>Recordings</TabSectionHeader>
      <div data-it="overviewTab-recordingList">
        {recordings?.map((recording, index) => {
          let link;
          if (!recording.recordingId) {
            link = <span>Recording #{index + 1}: Pending processing.</span>;
          } else if (!recording.fileIdentifier) {
            link = (
              <span>
                Recording #{index + 1}: Creating composition ({recording.status}).
              </span>
            );
          } else {
            link = (
              <>
                <a href={`/api/internal/notary/recordings/${recording.recordingId}/media`}>Recording #{index + 1}</a>:{' '}
                {formatIsoDateInLocalTime(recording.createdAt)}, {formatDuration(recording?.duration)}.
              </>
            );
          }
          return (
            <div key={recording.videoRoomId} data-it={`overviewTab-recording-${index}`}>
              {link}
            </div>
          );
        })}
      </div>
    </div>
  );
};
